// First-time family setup — shown after email verification when no family exists yet
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useTheme } from "../hooks/useTheme";
import type { ParentRole } from "@starquest/core";
import styles from "./Setup.module.css";

export default function Setup() {
  const navigate  = useNavigate();
  const theme = useTheme(); // base theme
  const setAuth   = useAuthStore((s) => s.setAuth);
  const email     = useAuthStore((s) => s.email) ?? localStorage.getItem("sq_email") ?? "";

  const [familyName,  setFamilyName]  = useState("");
  const [familyCode,  setFamilyCode]  = useState("");
  const [codeError,   setCodeError]   = useState("");
  const [kidName,     setKidName]     = useState("");
  const [kidAvatar,   setKidAvatar]   = useState("🧑‍🚀");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const CODE_EXAMPLES = ["SKYWALKERS", "GRYFFINDOR", "WAKANDAFAM", "TEAMSTARK", "THEAVENGERS", "HOUSESMITH", "JORDANFAM", "MUSTAFARS"];
  const [exampleIdx] = useState(() => Math.floor(Math.random() * CODE_EXAMPLES.length));

  const AVATARS = ["🧑‍🚀","🦸","🧙","🦄","🐉","🦊","🐼","🦁","🐯","🦋"];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!familyName.trim()) { setError("Family name is required"); return; }
    setError(""); setLoading(true);

    try {
      const family = await api.createFamily(familyName.trim(), familyCode.trim() || undefined) as { familyId: string; token: string; familyCode?: string };
      // Update auth with the real token (contains the new familyId) before any further API calls
      const payload = JSON.parse(atob(family.token.split(".")[1]));
      setAuth(family.token, family.familyId, email, payload.parentRole as ParentRole);
      if (kidName.trim()) {
        await api.createKid(family.familyId, { name: kidName.trim(), avatar: kidAvatar });
      }
      navigate("/parent");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Setup failed");
    } finally { setLoading(false); }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>🏡</div>
        <h2 className={styles.title}>Set up your family</h2>
        <p className={styles.sub}>You're in! Let's get your family ready for {theme.challengeNamePlural.toLowerCase()}.</p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.group}>
            <label className={styles.label}>Family name</label>
            <input
              className={styles.input}
              placeholder="e.g. The Johnsons"
              value={familyName} onChange={(e) => setFamilyName(e.target.value)}
              required autoFocus
            />
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Family code — kids use this to log in</label>
            <input
              className={styles.input}
              placeholder={`e.g. ${CODE_EXAMPLES[exampleIdx]}`}
              value={familyCode}
              onChange={(e) => {
                setCodeError("");
                setFamilyCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""));
              }}
            />
            <p style={{ fontSize: 11, color: "rgba(240,240,255,0.4)", marginTop: 4 }}>
              Letters and numbers only · no spaces · can be anything fun: a movie, a team, a character
            </p>
            {codeError && <p className={styles.error}>{codeError}</p>}
          </div>

          <div className={styles.group}>
            <label className={styles.label}>First {theme.kidRole.toUpperCase()} (OPTIONAL — ADD MORE LATER)</label>
            <input
              className={styles.input}
              placeholder="Kid's name"
              value={kidName} onChange={(e) => setKidName(e.target.value)}
            />
          </div>

          {kidName && (
            <div className={styles.group}>
              <label className={styles.label}>Explorer avatar</label>
              <div className={styles.avatars}>
                {AVATARS.map((a) => (
                  <button
                    key={a} type="button"
                    className={`${styles.avatar} ${kidAvatar === a ? styles.avatarActive : ""}`}
                    onClick={() => setKidAvatar(a)}
                  >{a}</button>
                ))}
              </div>
            </div>
          )}

          {error && <p className={styles.error}>{error}</p>}

          <button className={styles.btn} disabled={loading}>
            {loading ? "Creating…" : "Launch StarQuest 🚀"}
          </button>
        </form>
      </div>
    </div>
  );
}
